import * as echarts from 'echarts'

/**
 * 支持量热度图共享构建器。
 * 两套市场共用同一时间轴(FootBallRate 列表)；金额=支持量，赔率叠加在右侧轴。
 */
export const MARKETS = {
  had: [
    { key: 'h', label: '胜', amt: 'hmoney', odds: 'hadH', color: '#f87171' },
    { key: 'd', label: '平', amt: 'dmoney', odds: 'hadD', color: '#fbbf24' },
    { key: 'a', label: '负', amt: 'amoney', odds: 'hadA', color: '#38bdf8' }
  ],
  hhad: [
    { key: 'h', label: '让胜', amt: 'hhmoney', odds: 'hhadH', color: '#f87171' },
    { key: 'd', label: '让平', amt: 'dhmoney', odds: 'hhadD', color: '#fbbf24' },
    { key: 'a', label: '让负', amt: 'ahmoney', odds: 'hhadA', color: '#38bdf8' }
  ]
}

const parseNum = (v) => {
  const n = Number(v)
  return isNaN(n) || !isFinite(n) ? null : n
}

/** 金额轴缩写：>=1亿 → 亿；>=1万 → 万；>=1千 → k；否则原样 */
export const abbr = (n) => {
  const v = Number(n)
  if (isNaN(v) || !isFinite(v)) return '-'
  const abs = Math.abs(v)
  if (abs >= 1e8) return trimDec(v / 1e8) + '亿'
  if (abs >= 1e4) return trimDec(v / 1e4) + '万'
  if (abs >= 1e3) return trimDec(v / 1e3) + 'k'
  return String(Math.round(v))
}
const trimDec = (x) => String(Number(Number(x).toFixed(2)))

/** 某市场三条支持量/赔率数组与占比数组 */
const calc = (list, opts) => {
  const amts = opts.map(o => list.map(r => parseNum(r[o.amt]) || 0))
  const odds = opts.map(o => list.map(r => parseNum(r[o.odds])))
  const pcts = amts.map(arr => arr.map((a, i) => {
    const total = arr.length ? amts.reduce((s, ar) => s + (ar[i] || 0), 0) : 0
    return total > 0 ? Math.round((a / total) * 1000) / 10 : 0
  }))
  return { amts, odds, pcts }
}

const hexToRgba = (hex, a) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!m) return hex
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`
}

const fmtLabel = (ds) => {
  if (!ds) return ''
  const parts = String(ds).split(' ')
  const d = (parts[0] || '').split('-')
  const mmdd = d.length >= 3 ? `${d[1]}-${d[2]}` : (parts[0] || '')
  const t = (parts[1] || '').slice(0, 5)
  return t && t !== '00:00' ? mmdd + '\n' + t : mmdd
}

const THEME = {
  light: { axis: '#8c8c8c', split: 'rgba(0,0,0,0.08)', tooltipBg: '#fff', tooltipBorder: '#e5e7eb', tooltipText: '#374151' },
  dark: { axis: '#94a3b8', split: 'rgba(42,58,85,0.5)', tooltipBg: '#1a2234', tooltipBorder: '#2a3a55', tooltipText: '#e2e8f0' }
}

/**
 * @param list   FootBallRate 序列(时间升序)
 * @param market 'had' | 'hhad'
 * @param mode   'amount' 金额 | 'pct' 占比
 * @param dark   深色主题(全屏横屏用)
 * @param showOdds 是否叠加赔率虚线(右侧赔率轴)
 */
export function heatOption (list, market, mode, dark, showOdds = true) {
  const opts = MARKETS[market] || MARKETS.had
  const th = dark ? THEME.dark : THEME.light
  const labels = list.map(r => fmtLabel(r.dateString))
  const { amts, odds, pcts } = calc(list, opts)
  const isPct = mode === 'pct'

  // 左轴
  const yAxes = isPct
    ? [{ type: 'value', min: 0, max: 100, axisLabel: { color: th.axis, fontSize: 10, formatter: (v) => v + '%' }, splitLine: { lineStyle: { type: 'dashed', color: th.split } }, axisTick: { show: false } }]
    : [{ type: 'value', axisLabel: { color: th.axis, fontSize: 10, formatter: abbr }, splitLine: { lineStyle: { type: 'dashed', color: th.split } }, axisTick: { show: false } }]

  // 是否有有效赔率数据可画
  const hasOddsData = showOdds && opts.some((o, oi) => odds[oi].some(v => v != null))
  if (hasOddsData) {
    yAxes.push({
      type: 'value',
      scale: true,
      position: 'right',
      axisLabel: { color: th.axis, fontSize: 9, formatter: (v) => v.toFixed(2) },
      splitLine: { show: false },
      axisTick: { show: false }
    })
  }

  // 主体(金额曲线 / 占比堆叠带)
  const series = []
  opts.forEach((o, oi) => {
    const color = o.color
    if (isPct) {
      series.push({
        name: o.label,
        type: 'line',
        data: pcts[oi],
        smooth: true,
        showSymbol: false,
        stack: 'share',
        lineStyle: { width: 1, color: hexToRgba(color, 0.9) },
        itemStyle: { color },
        areaStyle: { color: hexToRgba(color, 0.82) },
        emphasis: { focus: 'series' }
      })
    } else {
      series.push({
        name: o.label,
        type: 'line',
        data: amts[oi],
        smooth: true,
        showSymbol: false,
        symbol: 'circle',
        lineStyle: { width: 2.5, color },
        itemStyle: { color },
        areaStyle: { color: hexToRgba(color, 0.16) },
        emphasis: { focus: 'series' }
      })
    }
  })

  // 赔率虚线(右轴)，与对应选项同色
  if (hasOddsData) {
    opts.forEach((o, oi) => {
      series.push({
        name: o.label + '赔率',
        type: 'line',
        data: odds[oi],
        yAxisIndex: 1,
        smooth: true,
        showSymbol: false,
        symbol: 'circle',
        lineStyle: { width: 1.3, type: 'dashed', color: o.color, opacity: 0.9 },
        itemStyle: { color: o.color },
        emphasis: { focus: 'series' },
        z: 3
      })
    })
  }

  const tooltip = {
    trigger: 'axis',
    backgroundColor: th.tooltipBg,
    borderColor: th.tooltipBorder,
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: th.tooltipText, fontSize: 12 },
    axisPointer: { type: 'line', lineStyle: { color: 'rgba(148,163,184,0.5)' } },
    formatter: (params) => {
      const idx = params[0].dataIndex
      const head = labels[idx] ? `<div style="font-weight:700;margin-bottom:5px;white-space:pre-line">${labels[idx]}</div>` : ''
      const rows = []
      opts.forEach((o, oi) => {
        const amt = amts[oi][idx] || 0
        const pct = pcts[oi][idx] || 0
        const mainTxt = isPct ? `${pct.toFixed(1)}%` : `${abbr(amt)}（${pct.toFixed(1)}%）`
        rows.push(rowHtml(o.color, o.label, mainTxt))
        if (hasOddsData) {
          const od = odds[oi][idx]
          rows.push(rowHtml(o.color, o.label + '赔率', od == null ? '-' : od.toFixed(2)))
        }
      })
      return head + rows.join('')
    }
  }

  const step = Math.max(0, Math.ceil(labels.length / 10) - 1)
  return {
    animationDuration: 200,
    tooltip,
    grid: { left: 52, right: hasOddsData ? 44 : 16, top: 14, bottom: 36 },
    xAxis: {
      type: 'category',
      data: labels,
      boundaryGap: false,
      axisLine: { lineStyle: { color: dark ? 'rgba(42,58,85,0.7)' : 'rgba(0,0,0,0.12)' } },
      axisTick: { show: false },
      axisLabel: { color: th.axis, fontSize: 9, interval: step, rotate: 30 }
    },
    yAxis: yAxes,
    dataZoom: [{ type: 'inside' }],
    series
  }
}

const rowHtml = (color, label, value) => `<div style="display:flex;align-items:center;gap:7px;line-height:1.7">
  <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color}"></span>
  <span>${label}</span>
  <b style="margin-left:auto">${value}</b></div>`

/** 渲染到容器(先 dispose)，返回实例 */
export function renderHeat (el, list, market, mode, dark, showOdds) {
  if (!el) return null
  const old = echarts.getInstanceByDom(el)
  if (old) old.dispose()
  if (!list || !list.length) return null
  const chart = echarts.init(el)
  chart.setOption(heatOption(list, market, mode, dark, showOdds))
  return chart
}
